import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import MixpanelService from '../../../../services/MixpanelService';
import MixpanelConfig from '../../../../_shared/constants/mixpanelConfig';

import useGoogleMaps from '../../../../_shared/hooks/maps/useGoogleMaps';
import TradeInService from '../../../../services/TradeInService';
import {
  CANADA_BOUNDS,
  DEFAULT_MAP_CENTER,
  DEFAULT_ZOOM_LEVEL,
  MAXIMUM_ZOOM_WITH_LABEL,
  provinceShortName
} from '../../../../_shared/constants/retail';
import ROUTES from '../../../../components/Routes/constants';
import MarkerIcon from '../../../../assets/external/googlemaps/icon-location-single-selected.png';
import MarkerIconMultiple from '../../../../assets/external/googlemaps/icon-location-multiple-unselected.png';
import Alert from '../../../../_shared/components/Alert';

import { MapContainer, MapTile, DisplayInfo, MapOverlay } from './Styles';
import TRToast from '../../../../_shared/helpers/TRToast';

const propTypes = {
  filterMetaData: PropTypes.shape({
    isFranchiseDealer: PropTypes.bool,
    hasTradeIn: PropTypes.bool
  }).isRequired,
  isMobile: PropTypes.bool.isRequired,
  onSelectDealer: PropTypes.func
};

const defaultProps = {
  onSelectDealer: () => null
};

/**
 * Returns the radius in meters for the pixels in the current zoom level
 */
function metersPerPx(zoom, latitude) {
  const maxResolution = 156543.03392;

  return (maxResolution * Math.cos((latitude * Math.PI) / 180)) / 2 ** zoom;
}

function getUserLocation() {
  const { location } = TradeInService.getUserTradeInProps() ?? {};

  if (!location?.latitude || !location?.longitude) {
    return {
      lat: DEFAULT_MAP_CENTER.lat,
      lng: DEFAULT_MAP_CENTER.lng
    };
  }

  return {
    lat: location.latitude,
    lng: location.longitude
  };
}

const DealerMap = ({ filterMetaData, isMobile, onSelectDealer }) => {
  const mixpanel = MixpanelService.get();
  const [userLocation] = useState(getUserLocation());
  const { ref, map, google } = useGoogleMaps({
    zoom: DEFAULT_ZOOM_LEVEL,
    center: {
      lat: userLocation.lat,
      lng: userLocation.lng
    },
    restriction: {
      latLngBounds: CANADA_BOUNDS,
      strictBounds: true
    },
    disableDefaultUI: true,
    zoomControl: true,
    zoomControlOptions: {
      position: 7.0 // TOP RIGHT
    }
  });

  const history = useHistory();
  const [infoData, setInfoData] = useState({});
  const currentMarkers = useRef([]);
  const currentMarkerIds = useRef([]);
  const cluster = useRef(null);
  const dealerLocationPoints = useRef([]);
  const lowestSeenZoom = useRef(DEFAULT_ZOOM_LEVEL);

  const onDealerPick = ({
    dealerName,
    dealerId,
    dealerCity,
    dealerProvince
  }) => {
    if (!dealerId) {
      return;
    }

    // store dealerId in sessionstorage
    TradeInService.updateUserTradeInProps({
      dealerId,
      dealerDisplayName: dealerName
    });

    mixpanel.track(MixpanelConfig.names.tiahPickDealer, {
      dealer_id: dealerId,
      dealer: dealerName,
      dealer_location: dealerCity,
      dealer_province: dealerProvince
    });

    history.push(ROUTES.TIAH_MOBILE_NUMBER);
  };

  const clearInfoData = () => {
    setInfoData([]);
  };

  const getLabels = (includeLabels, dealer) => {
    if (!includeLabels) {
      return {};
    }
    if (dealer.length > 1) {
      return {
        label: {
          color: '#fff',
          fontWeight: 'bold',
          fontSize: '11px',
          text: `${dealer.length}`,
          className: 'marker-multiple-position'
        },
        icon: {
          url: MarkerIconMultiple
        }
      };
    }

    return {
      label: {
        color: '#000',
        fontWeight: 'bold',
        fontSize: '14px',
        text: dealer[0].displayName,
        className: 'marker-position'
      },
      icon: {
        url: MarkerIcon
      }
    };
  };

  const createMarkers = (dealerLocations, zoomLevel) => {
    const includeLabels = zoomLevel > MAXIMUM_ZOOM_WITH_LABEL;

    currentMarkers.current = [];
    currentMarkerIds.current = [];

    const locationMap = new Map();

    dealerLocations.forEach(dealer => {
      const key = `${dealer.latitude}-${dealer.longitude}`;

      if (!locationMap.has(key)) {
        locationMap.set(key, [{ ...dealer }]);
      } else {
        const entries = locationMap.get(key);
        entries.push({ ...dealer });
        locationMap.set(key, entries);
      }
    });

    locationMap.forEach(dealerValue => {
      const { latitude, longitude } = dealerValue[0];

      const marker = new google.maps.Marker({
        position: {
          lat: latitude,
          lng: longitude
        },
        ...getLabels(includeLabels, dealerValue)
      });

      marker.metaData = [];

      dealerValue.forEach(dealer => {
        const shortName = provinceShortName(dealer.province);

        marker.metaData.push({
          dealerId: dealer.id,
          dealerName: dealer.displayName,
          address: `${dealer.street}, ${dealer.city}, ${shortName} ${dealer.zip}`,
          city: dealer.city,
          province: shortName
        });
      });

      google.maps.event.addListener(marker, 'click', () => {
        onSelectDealer();
        setInfoData(marker.metaData);
      });

      currentMarkers.current.push(marker);
    });

    if (cluster?.current) {
      cluster.current.clearMarkers();
    }

    cluster.current = new MarkerClusterer(map, currentMarkers.current, {
      maxZoom: 13,
      gridSize: 120,
      styles: [
        {
          url: MarkerIconMultiple,
          textSize: 12,
          width: 42,
          height: 42,
          textColor: 'white',
          className: 'cluster-style'
        }
      ],
      averageCenter: true
    });
  };

  const getDealerLocations = async (
    isFranchiseDealerOnly,
    zoom,
    lat,
    radius
  ) => {
    let dealerLocations;
    try {
      dealerLocations = await TradeInService.getDealerLocations({
        isFranchiseDealerOnly,
        radius,
        point: {
          latitude: userLocation.lat,
          longitude: userLocation.lng
        }
      });

      dealerLocationPoints.current = dealerLocations.map(_dealerLoc => {
        return {
          ..._dealerLoc,
          zoomLevel: zoom
        };
      });
    } catch (e) {
      TRToast(
        <Alert
          variant="error"
          messages={[<FormattedMessage id="tiah.map.error.message" />]}
        />
      );
      dealerLocationPoints.current = [];
    }

    return dealerLocationPoints.current;
  };

  const isGoogleMapsReady = useCallback(() => {
    return !!(google && map);
  }, [google, map]);

  // Register zoom event listener
  useEffect(() => {
    if (!isGoogleMapsReady()) {
      return;
    }

    async function onZoomChange() {
      const currentZoom = map.getZoom();

      // always use the highest seen zoom
      lowestSeenZoom.current =
        lowestSeenZoom.current < currentZoom
          ? lowestSeenZoom.current
          : currentZoom;

      const lat = map.getCenter().lat();
      const radius = metersPerPx(lowestSeenZoom.current, lat);
      const dealerLocations = await getDealerLocations(
        filterMetaData.isFranchiseDealer,
        lowestSeenZoom.current,
        lat,
        radius
      );

      createMarkers(dealerLocations, currentZoom);
    }

    const zoomEventListener = google.maps.event.addListener(
      map,
      'zoom_changed',
      onZoomChange
    );

    // eslint-disable-next-line consistent-return
    return () => google.maps.event.removeListener(zoomEventListener);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterMetaData.isFranchiseDealer, isGoogleMapsReady, google, map]);

  // Fetch dealers on initial load
  useEffect(() => {
    async function initiaLoad() {
      const newZoom = lowestSeenZoom.current;

      const lat = map.getCenter().lat();
      const radius = metersPerPx(newZoom, lat);

      const dealerLocations = await getDealerLocations(
        filterMetaData.isFranchiseDealer,
        newZoom,
        lat,
        radius
      );

      createMarkers(dealerLocations, map.getZoom());
    }

    if (isGoogleMapsReady()) {
      initiaLoad();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterMetaData.isFranchiseDealer, isGoogleMapsReady, google, map]);

  useEffect(() => {
    function onClick() {
      clearInfoData();
    }

    if (!isGoogleMapsReady()) {
      return undefined;
    }

    const clickListener = google.maps.event.addListener(map, 'click', onClick);

    return () => google.maps.event.removeListener(clickListener);
  }, [map, google, isGoogleMapsReady]);

  // clear info data if filter value changes
  useEffect(() => {
    clearInfoData();
  }, [filterMetaData.isFranchiseDealer]);

  return (
    <MapContainer overlay={!filterMetaData.hasTradeIn}>
      {!filterMetaData.hasTradeIn && <MapOverlay />}
      <MapTile ref={ref} />

      {infoData.length > 0 && (
        <DisplayInfo>
          {(infoData.length > 1 || isMobile) && (
            <DisplayInfo.Size>
              {isMobile && <DisplayInfo.Close clickHandler={clearInfoData} />}
              {infoData.length > 1 && `${infoData.length} DEALERS`}
            </DisplayInfo.Size>
          )}

          {infoData.map(info => {
            return (
              <DisplayInfo.Item
                key={info.dealerId}
                variant={infoData.length > 1 ? 'multiple' : 'single'}
              >
                <DisplayInfo.Heading>{info?.dealerName}</DisplayInfo.Heading>
                <DisplayInfo.SubHeading>{info?.address}</DisplayInfo.SubHeading>
                <DisplayInfo.Confirm>
                  &#129309;{' '}
                  <FormattedMessage id="tiah.map.info.valid.traderev" />
                </DisplayInfo.Confirm>
                <DisplayInfo.Button
                  onClick={() =>
                    onDealerPick({
                      dealerName: info?.dealerName,
                      dealerId: info?.dealerId,
                      dealerCity: info?.city,
                      dealerProvince: info?.province
                    })
                  }
                >
                  <FormattedMessage id="tiah.map.info.button.msg" />
                </DisplayInfo.Button>
              </DisplayInfo.Item>
            );
          })}
        </DisplayInfo>
      )}
    </MapContainer>
  );
};

DealerMap.propTypes = propTypes;
DealerMap.defaultProps = defaultProps;

export default DealerMap;
